import React, { useState } from "react";
import { Mail, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const TikTokIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.44z" />
  </svg>
);

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — Colleen will be in touch.");
      setForm({ name: "", email: "", message: "" });
      setSent(true);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Couldn't send right now — please try again."
      );
    }
    setSending(false);
  };

  return (
    <div
      data-testid="contact-page"
      className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20"
    >
      <p className="label-eyebrow fade-up">Get in Touch</p>
      <h1 className="font-serif text-5xl md:text-6xl font-light text-[#2D2926] mt-4 tracking-tight fade-up-delay-1">
        Contact Colleen
      </h1>
      <p className="text-[#5B5653] mt-6 max-w-2xl text-base leading-relaxed fade-up-delay-2">
        For questions about a particular painting, press enquiries, or anything else, please
        reach out. Colleen reads every message personally.
      </p>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
        <div className="md:col-span-5 space-y-10">
          <div className="flex items-start gap-4">
            <Mail className="w-5 h-5 text-[#E25C46] mt-1" />
            <div>
              <p className="label-eyebrow mb-1">Email</p>
              <a
                data-testid="contact-email-link"
                href="mailto:colleen.prosser@outlook.com"
                className="text-[#2D2926] hover:text-[#E25C46] transition-colors text-lg"
              >
                colleen.prosser@outlook.com
              </a>
              <p className="text-sm text-[#5B5653] mt-1">
                Typical reply within 2 business days.
              </p>
            </div>
          </div>

          <div className="pt-6 border-t border-[#E5D8BD]">
            <p className="label-eyebrow mb-4">Follow</p>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/prosser_colleen"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                aria-label="Colleen Prosser on Instagram"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#E5D8BD] text-[#2D2926] hover:text-[#E25C46] hover:border-[#E25C46] transition-colors text-sm"
              >
                <Instagram className="w-4 h-4" />
                <span>@prosser_colleen</span>
              </a>
              <a
                href="https://www.tiktok.com/@colleenart0"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-tiktok"
                aria-label="Colleen Prosser on TikTok"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#E5D8BD] text-[#2D2926] hover:text-[#E25C46] hover:border-[#E25C46] transition-colors text-sm"
              >
                <TikTokIcon className="w-4 h-4" />
                <span>@colleenart0</span>
              </a>
            </div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={handleSubmit}
          className="md:col-span-7 space-y-8"
        >
          <label className="block">
            <span className="label-eyebrow block mb-2">Your name</span>
            <input
              data-testid="contact-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-line"
            />
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-2">Email</span>
            <input
              data-testid="contact-email-input"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input-line"
            />
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-2">Message</span>
            <textarea
              data-testid="contact-message"
              required
              rows={7}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="input-line resize-none"
            />
          </label>
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={sending}
            className="btn-primary"
          >
            {sending ? "Sending…" : sent ? "Send another" : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
}
